/**
 * A node the user picked as an AI context/edit target.
 *
 * Two families:
 * - **Editable** (goal/task/subtask): the existing AI edit patch path can
 *   directly modify these. `isEditMode` in `AiGeneratorPanel` is driven by
 *   the presence of these.
 * - **Mention-only** (checklist_item/upload/meeting/report/form): fold into
 *   the prompt as context, but never dispatched through the edit patch API.
 *   Adding a mention does NOT flip Send to Build; discussion stays cheap.
 */
export type WorkspacePlanMode = 'EDIT' | 'CREATE'

export type EditTarget =
  | { kind: 'goal'; goalId: string; label: string; planMode?: WorkspacePlanMode }
  | { kind: 'task'; goalId: string; taskId: string; label: string; planMode?: WorkspacePlanMode }
  | { kind: 'subtask'; goalId: string; taskId: string; subTaskId: string; label: string; planMode?: WorkspacePlanMode }
  | {
      kind: 'checklist_item'
      goalId: string
      taskId: string
      subTaskId?: string
      itemId: string
      label: string
    }
  | { kind: 'upload'; assetId: string; label: string }
  | { kind: 'meeting'; meetingId: string; label: string }
  | { kind: 'report'; reportId: string; label: string }
  | { kind: 'form'; formId: string; label: string }

/** Stable identity for a target — used for selection membership + dedup. */
export const targetKey = (target: EditTarget): string => {
  switch (target.kind) {
    case 'goal':
      return `g:${target.goalId}`
    case 'task':
      return `t:${target.taskId}`
    case 'subtask':
      return `s:${target.subTaskId}`
    case 'checklist_item':
      return `c:${target.itemId}`
    case 'upload':
      return `u:${target.assetId}`
    case 'meeting':
      return `m:${target.meetingId}`
    case 'report':
      return `r:${target.reportId}`
    case 'form':
      return `f:${target.formId}`
  }
}

/**
 * True when this target is a direct edit destination (goal/task/subtask/checklist_item).
 * False for mention-only kinds — used by the composer to keep the
 * one-goal / three-target cap focused on editable targets, and by the
 * runner to skip non-editable kinds during patch dispatch.
 */
export const isEditableTarget = (
  target: EditTarget,
): target is Extract<EditTarget, { kind: 'goal' | 'task' | 'subtask' | 'checklist_item' }> => (
  target.kind === 'goal' || target.kind === 'task' || target.kind === 'subtask' || target.kind === 'checklist_item'
)

/** Only structural references may seed a fully separate plan. */
export const isCreatePlanReference = (target: EditTarget): boolean => (
  (target.kind === 'goal' || target.kind === 'task' || target.kind === 'subtask')
  && target.planMode === 'CREATE'
)
